import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { Message } from 'primeng/api';
import { Project } from 'src/app/shared/models/project';
import { CustomTabMenuItem } from 'src/app/shared/models/custom-tab-menu-item';
import { Constants } from 'src/app/shared/utils/constants';
import { UserLogicService } from 'src/app/shared/services/user.logic.service';
import { firstValueFrom, take } from 'rxjs';
import { DisciplineApiService } from 'src/app/shared/services/echo/discipline.api.service';
import { Discipline } from 'src/app/shared/models/echo/discipline';
import { EPAProjectRunService } from '../esa-output/project-status/esa-project-run.api.service';
import { ESATabService } from './esa-tab.service';
import { UdlControlledVocabularyService } from '../udl-controlled-vocabulary.service';

@Component({
  selector: 'app-udl-overlap-tool',
  templateUrl: './udl-overlap-tool.component.html',
  styleUrls: ['./udl-overlap-tool.component.css']
})
export class UDLOverlapToolComponent implements OnInit {
  activeItem!: CustomTabMenuItem;
  message!: Message;
  loading: boolean = false;
  nextIndex?: number;
  selectedProject?: Project;
  selectedProjectStatus?: number;
  isSetupValid: boolean = false;
  destroyRef = inject(DestroyRef)
  pwcFlag: boolean = false;
  isProjectOwnershipValid: boolean = true;
  isProjectRunning: boolean = false;
  canMakeEdits: boolean = false;
  columnDef: any[] = [];
  disciplinePk: number = 0;
  showLoadingForOutput: boolean = true;
  vocabularyLoaded: boolean = false;

  constructor(public tabService: ESATabService,
              private selectedProjectApiService: SelectedProjectApiService,
              private userLogicService:UserLogicService,
              private disciplineApiService: DisciplineApiService,
              private cdr: ChangeDetectorRef,
              private projectRunService: EPAProjectRunService,
              private controlledVocabularyService: UdlControlledVocabularyService
            ) { 
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    
    await this.controlledVocabularyService.WaitTillLoaded();

    await this.getDiscipline();
    this.initSubscribes();
  }

  async getDiscipline(): Promise<void> {
    await firstValueFrom(this.disciplineApiService.getDisciplineByName(Constants.DISCIPLINE_IDENTITIES.ENVIRONMENTAL_E_FATE).pipe(take(1))).then((discipline: Discipline) =>{
        this.disciplinePk = discipline?.disciplinePk ?? -1;
    });
  }

  onActiveIndexChange(index: number) {
    this.nextIndex = index;
    this.tabService.setActiveIndexChanged(true, index);

    const nextItem = this.tabService.items.find(i => i.index === index);
    if (nextItem) {
      this.tabService.setActiveItem(nextItem);
    }

    if(index ==  Constants.TAB_INDEX.OUTPUT){
      if(!this.showLoadingForOutput){
        this.loading = false;
      }
    }

    this.cdr.detectChanges();
  }

  initSubscribes(): void {
    this.tabService.activeItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(activeItem => {
      if (activeItem) {
        this.activeItem = activeItem;
        this.nextIndex = activeItem.index;
      }
    });

    this.selectedProjectApiService.selectedProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project?: Project) => {
      this.selectedProject = project;
      this.isProjectOwnershipValid = this.selectedProject ? this.userLogicService.verifyProjectOwnership(this.selectedProject?.createdBy) : true;
      this.updateCanMakeEdits();
      this.onActiveIndexChange(0);
      this.isPWC();
    });

    this.projectRunService.projectStatusUpdatedEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((statusId: number) => { 
      this.selectedProjectStatus = statusId;
      if (this.selectedProject) this.selectedProject.status = statusId;
    });

    this.tabService.isLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isLoading => {
      this.loading = isLoading;
    });

    this.tabService.success.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(success => {
      this.loading = false;
      if (success) {
        const nextItem = this.tabService.items.find(item => item.index == this.nextIndex);
        if (nextItem)
          this.tabService.setActiveItem(nextItem);
      }
    });

    this.projectRunService.isProjectRunningEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isRunning: boolean) => {
      this.isProjectRunning = isRunning
      this.updateCanMakeEdits();
     });
  }

  validateSetup(event: boolean) {
    this.isSetupValid = event;
    this.cdr.detectChanges();
  }

  isPWC() {
    this.pwcFlag = this.selectedProject?.projectXCompoundXModel?.map(a => a.ModelName).includes(Constants.MODELS.PWC) ?? false;
  }

  onUpdateLoadingStatus(loadingStatus: boolean) {
    this.loading = loadingStatus;
  }

  onShowLoadingForOutput(showLoadingForOutput: boolean) {
    this.showLoadingForOutput = showLoadingForOutput;
  }

  updateCanMakeEdits() {
    this.canMakeEdits = this.isProjectOwnershipValid && !this.isProjectRunning;
  }
}
